import { ChevronDownIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { LayoutCard } from "src/components/layout";
import { CONSTANTS_STRINGS } from "src/constants/strings";
import { formatBRCurrency } from "src/lib/format";
import { useCheckoutDataContext } from "src/providers/SharedDataProvider";
import {
  BodyLBold,
  HeadlineSize8Medium,
  HeadlineSize9Medium,
} from "../typographic";
import { SummaryItem } from "./SummaryItem";

type InsuranceDisplayInfo = {
  type: string;
  description: string;
  totalDescription: string;
  totalAmount: number;
};

const translateInsurance = (
  type: string,
  changePaymentMethod: boolean,
  checkoutPreferencesTotalAmount: number,
  subscriptionBillingCycleTotalAmount: number,
): InsuranceDisplayInfo => {
  const normalizedType = type.toUpperCase();
  const isMonthly =
    normalizedType === "LIFE" ||
    normalizedType === "VIDA" ||
    changePaymentMethod;

  const translations: Record<string, { type: string; description: string }> = {
    AUTO: { type: "AUTO", description: "Seguro Automóvel" },
    LIFE: { type: "VIDA", description: "Seguro de Vida" },
    VIDA: { type: "VIDA", description: "Seguro de Vida" },
    HOME: { type: "RESIDENCIAL", description: "Seguro Residencial" },
    RESIDENTIAL: { type: "RESIDENCIAL", description: "Seguro Residencial" },
    RESIDENCIAL: { type: "RESIDENCIAL", description: "Seguro Residencial" },
  };

  const { type: displayType, description } = translations[normalizedType] ?? {
    type,
    description: type,
  };

  return {
    type: displayType,
    description,
    totalDescription: isMonthly ? "Valor Mensal" : "Valor Total",
    totalAmount: isMonthly
      ? subscriptionBillingCycleTotalAmount
      : checkoutPreferencesTotalAmount,
  };
};

const Summary: React.FC = () => {
  const { pathname } = useLocation();
  const checkout = useCheckoutDataContext();
  const pathParts = pathname.split("/").filter(Boolean);

  const isRenderOpen = pathParts.length <= 1;
  const [isOpen, setIsOpen] = useState(isRenderOpen);

  const cars = checkout?.insurance?.policyInformation?.cars;

  const subscriptionBillingCycleAmount =
    checkout?.subscriptionBillingCycle?.totalAmount ?? 0;
  const checkoutPreferencesTotalAmount =
    checkout?.checkoutPreferences?.totalAmount ?? 0;
  const lifeInsuranceAmount = checkout?.items[0]?.price ?? 0;
  const insuranceTypeTitle =
    checkout?.insurance?.type?.name ?? CONSTANTS_STRINGS.NO_NAME_TITLE;
  const changePaymentMethod = checkout?.changePaymentMethod ?? false;
  const isLifeInsurance =
    insuranceTypeTitle.toUpperCase() === "LIFE" ||
    insuranceTypeTitle.toUpperCase() === "VIDA";
  const monthlyAmount = isLifeInsurance
    ? lifeInsuranceAmount
    : subscriptionBillingCycleAmount;
  const insuranceType = translateInsurance(
    insuranceTypeTitle,
    changePaymentMethod,
    checkoutPreferencesTotalAmount,
    monthlyAmount,
  );
  const document = checkout?.customer?.document ?? CONSTANTS_STRINGS.EMPTY;
  const brokerName =
    checkout?.insurance?.broker?.name ?? CONSTANTS_STRINGS.NO_NAME_TITLE;
  const customerName =
    checkout?.customer?.name ?? CONSTANTS_STRINGS.NO_NAME_TITLE;

  const insuranceItems = useMemo(() => {
    return cars?.map(
      (car) => `${car?.model} ${car.year} placa: ${car.licensePlate}`,
    );
  }, [cars]);

  const insuranceItemsList = useMemo(() => {
    return (
      <div className="flex flex-col">
        {insuranceItems?.map((item, index) => (
          <BodyLBold key={index} className="text-left">
            {item}
          </BodyLBold>
        ))}
      </div>
    );
  }, [insuranceItems]);

  const insuranceItemsTitle = useMemo(() => {
    return insuranceItems.length > 1
      ? CONSTANTS_STRINGS.SUMMARY_INSURANCE_ITEMS_PLURAL_TITLE
      : CONSTANTS_STRINGS.SUMMARY_INSURANCE_ITEMS_TITLE;
  }, [insuranceItems]);

  useEffect(() => {
    setIsOpen(isRenderOpen);
  }, [isRenderOpen]);

  return (
    <article className="flex flex-col">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="hover:border-b-secondary-4 flex items-center justify-between border border-transparent duration-200 hover:cursor-pointer"
      >
        <HeadlineSize8Medium className="text-left">
          {CONSTANTS_STRINGS.SUMMARY}
        </HeadlineSize8Medium>
        <ChevronDownIcon
          className={`text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200 ${
            isOpen ? "rotate-0" : "rotate-180"
          }`}
        />
      </button>
      <LayoutCard
        className={`mt-4 flex flex-col gap-6 overflow-hidden transition-all duration-300 ease-in-out lg:grid lg:grid-cols-3 ${
          isOpen ? "opacity-100" : "m-0! max-h-0 gap-0 py-0! opacity-0"
        }`}
      >
        <HeadlineSize9Medium className="text-left lg:col-span-3">
          {insuranceType?.description}
        </HeadlineSize9Medium>
        <SummaryItem
          caption={
            CONSTANTS_STRINGS.SUMMARY_RESPONSIBLE_FINANCIAL_DOCUMENT_TITLE
          }
          value={document}
        />
        <SummaryItem
          caption={CONSTANTS_STRINGS.SUMMARY_RESPONSIBLE_FINANCIAL_NAME_TITLE}
          value={customerName}
        />
        <SummaryItem
          caption={CONSTANTS_STRINGS.SUMMARY_BROKER_NAME_TITLE}
          value={brokerName}
          className="lg:col-start-1"
        />
        <SummaryItem
          caption={CONSTANTS_STRINGS.SUMMARY_INSURANCE_TYPE_TITLE}
          value={insuranceType?.type}
        />
        {cars?.length > 0 && (
          <SummaryItem caption={insuranceItemsTitle}>
            {insuranceItemsList}
          </SummaryItem>
        )}
        <SummaryItem
          caption={insuranceType.totalDescription}
          value={formatBRCurrency(insuranceType.totalAmount)}
          className="gap-5"
          valueClassName="text-[24px] leading-[36px] gap-6"
        />
      </LayoutCard>
    </article>
  );
};

export default Summary;
